import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { useThemeProps } from "@mui/material";
import { useSlotProps } from "../../../base/index.js";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ActionContainer } from "./components/ActionContainer.js";
import { BottomToolbar } from "./components/BottomToolbar.js";
import { ContentEditable } from "./components/ContentEditable.js";
import { EditorContent } from "./components/EditorContent.js";
import { EditorScroller } from "./components/EditorScroller.js";
import { Footer } from "./components/Footer.js";
import { HelperText } from "./components/HelperText.js";
import { Placeholder } from "./components/Placeholder.js";
import { QuickInsertToolbar } from "./components/QuickInsertToolbar.js";
import { Root } from "./components/Root.js";
import { Toolbar } from "./components/Toolbar.js";
import { editorName } from "./classes.js";
import { useEditor } from "./hooks/useEditor.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
const Editor = forwardRef(function Editor2(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: editorName
  });
  const {
    sx,
    texts: { quickInsertMenuTooltip = "Insert", toolbarOverflowMenuTooltip = "More formatting options" } = {},
    error,
    testId = `${editorName}-Root`,
    variant = "rich",
    actions,
    disabled,
    children,
    onChange,
    className = "",
    autoFocus,
    components,
    helperText,
    userFooter,
    initialConfig,
    componentsProps,
    placeholderText,
    editorThemeStyles,
    ...rest
  } = props;
  const { editorChildren, cssRules, footerActions, footerContents, toolbarVisible, enableFooterPadding } = useEditor(props);
  const isRichText = variant === "rich";
  const isPlainText = variant === "plain";
  const ownerState = props;
  const utilityClasses = useUtilityClasses(props);
  const rootSlotProps = useSlotProps({
    className: [className, utilityClasses.root],
    ownerState,
    elementType: Root,
    additionalProps: {
      sx,
      as: components?.Root,
      ref,
      cssRules,
      "data-tidal": editorName,
      "data-testid": testId
    },
    externalSlotProps: componentsProps?.root,
    externalForwardedProps: rest
  });
  const toolbarSlotProps = useSlotProps({
    className: utilityClasses.toolbar,
    ownerState,
    elementType: Toolbar,
    additionalProps: {
      as: components?.Toolbar,
      overflowTooltip: toolbarOverflowMenuTooltip
    },
    externalSlotProps: componentsProps?.toolbar
  });
  const editorScrollerSlotProps = useSlotProps({
    className: utilityClasses.editorScroller,
    ownerState,
    elementType: EditorScroller,
    additionalProps: { as: components?.EditorScroller },
    externalSlotProps: componentsProps?.editorScroller
  });
  const placeholderSlotProps = useSlotProps({
    className: utilityClasses.placeholder,
    ownerState,
    elementType: Placeholder,
    additionalProps: {
      as: components?.Placeholder,
      variant: "body1"
    },
    externalSlotProps: componentsProps?.placeholder
  });
  const editorContentSlotProps = useSlotProps({
    className: utilityClasses.editorContent,
    ownerState,
    elementType: EditorContent,
    additionalProps: { as: components?.EditorContent },
    externalSlotProps: componentsProps?.editorContent
  });
  const contentEditableSlotProps = useSlotProps({
    className: utilityClasses.contentEditable,
    ownerState,
    elementType: ContentEditable,
    externalSlotProps: componentsProps?.contentEditable
  });
  const footerSlotProps = useSlotProps({
    className: utilityClasses.footer,
    ownerState,
    elementType: Footer,
    additionalProps: { as: components?.Footer, enableFooterPadding },
    externalSlotProps: componentsProps?.footer
  });
  const quickInsertToolbarSlotProps = useSlotProps({
    className: utilityClasses.quickInsertToolbar,
    ownerState,
    elementType: QuickInsertToolbar,
    additionalProps: {
      as: components?.QuickInsertToolbar,
      disabled,
      menuTooltip: quickInsertMenuTooltip
    },
    externalSlotProps: componentsProps?.quickInsertToolbar
  });
  const helperTextSlotProps = useSlotProps({
    className: utilityClasses.helperText,
    ownerState,
    elementType: HelperText,
    additionalProps: {
      as: components?.HelperText,
      error,
      variant: "outlined",
      disabled
    },
    externalSlotProps: componentsProps?.helperText
  });
  const actionContainerSlotProps = useSlotProps({
    className: utilityClasses.actionContainer,
    ownerState,
    elementType: ActionContainer,
    additionalProps: { as: components?.ActionContainer },
    externalSlotProps: componentsProps?.actionContainer
  });
  const bottomToolbarSlotProps = useSlotProps({
    className: utilityClasses.bottomToolbar,
    ownerState,
    elementType: BottomToolbar,
    additionalProps: { as: components?.BottomToolbar },
    externalSlotProps: componentsProps?.bottomToolbar
  });
  const placeholder = placeholderText ? /* @__PURE__ */ jsx(Placeholder, { ...placeholderSlotProps, children: placeholderText }) : null;
  const contentEditable = /* @__PURE__ */ jsx(ContentEditable, { ...contentEditableSlotProps });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, children: [
    /* @__PURE__ */ jsxs(EditorContent, { ...editorContentSlotProps, children: [
      toolbarVisible && /* @__PURE__ */ jsx(Toolbar, { ...toolbarSlotProps }),
      editorChildren.outsideScrollAbove,
      /* @__PURE__ */ jsxs(EditorScroller, { ...editorScrollerSlotProps, children: [
        editorChildren.insideScrollAbove,
        isRichText && /* @__PURE__ */ jsx(
          RichTextPlugin,
          {
            placeholder,
            ErrorBoundary: LexicalErrorBoundary,
            contentEditable
          }
        ),
        isPlainText && /* @__PURE__ */ jsx(
          PlainTextPlugin,
          {
            placeholder,
            ErrorBoundary: LexicalErrorBoundary,
            contentEditable
          }
        ),
        editorChildren.insideScrollBelow
      ] }),
      editorChildren.outsideScrollBelow,
      autoFocus && /* @__PURE__ */ jsx(AutoFocusPlugin, {}),
      onChange && /* @__PURE__ */ jsx(OnChangePlugin, { onChange }),
      children,
      userFooter ? userFooter : /* @__PURE__ */ jsxs(Footer, { ...footerSlotProps, children: [
        /* @__PURE__ */ jsxs(BottomToolbar, { ...bottomToolbarSlotProps, children: [
          /* @__PURE__ */ jsx(QuickInsertToolbar, { ...quickInsertToolbarSlotProps }),
          /* @__PURE__ */ jsx(ActionContainer, { ...actionContainerSlotProps, children: footerActions })
        ] }),
        footerContents
      ] })
    ] }),
    helperText && /* @__PURE__ */ jsx(HelperText, { ...helperTextSlotProps, children: helperText })
  ] });
});
export {
  Editor
};
//# sourceMappingURL=Editor.js.map
